/** @format */

import {
  AfterViewInit,
  Component,
  DOCUMENT,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AnalyticsService,
  DynamicTemplateLoader,
  Store,
  TaggingService,
  WINDOW,
  triggerTargetView,
} from '@shared/data-access/services';
import { MetaService, SeoService } from '@seo/data-access';

import { ActivatedRoute } from '@angular/router';
import { Chain } from '@shared/data-access/interfaces';
import { ComponentBindings } from '../../../app.component-modules';
import { HomeDefault } from './home-default';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'home-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  providers: [HomeDefault],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomeComponent implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly window = inject(WINDOW);
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly taggingService = inject(TaggingService);
  private readonly homeDefault = inject(HomeDefault);
  public readonly store = inject(Store);
  private readonly chain = inject(Chain);
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly metaService = inject(MetaService);
  private readonly seoService = inject(SeoService);
  private readonly dynamicTemplateLoader = inject(DynamicTemplateLoader);

  public templateData: any = {};
  public componentList: any;
  public surveyUrl: string;
  public DSGShoppingPathJS;
  public certonaSectionNumber;
  public certonaRecommendationsResponse: any;

  private elementDataSubscription: Subscription;
  private templateLoaderSubscription: Subscription;

  // Flags
  public flagSurvey: string;

  public components: any;

  @ViewChild('templateOutlet', { read: ViewContainerRef })
  templateOutlet: ViewContainerRef;

  children: any[] = [];
  pageType = 'home';
  searchDexResponse: any;

  /**
   * @type boolean
   *
   * @description
   * Boolean for displaying featuredSearchdex categories
   */
  public showFeaturedCategories = false;

  constructor() {
    // TODO: figure out survey this.flagSurvey = false;
    this.components = ComponentBindings();
  }

  ngAfterViewInit() {
    this.route.data.subscribe((data) => {
      this.setTemplateData(data);
    });

    if (typeof this.window.certonaRecommendations === 'undefined') {
      // Assign the Component method to the global Window so that Certona can execute it, passing in `this` as context
      this.window.certonaRecommendations = this.getCertonaRecommendations.bind(this);
      this.window.DSGShoppingPathJS = {
        certonaRecommendations: this.getCertonaRecommendations.bind(this),
      };
    }
  }

  ngOnDestroy() {
    if (this.elementDataSubscription) {
      this.elementDataSubscription.unsubscribe();
    }

    if (this.templateLoaderSubscription) {
      this.templateLoaderSubscription.unsubscribe();
    }
  }

  public setTemplateData(data) {
    this.templateData =
      data.homrData && data.homrData.content
        ? data.homrData
        : this.getEvergreen(this.chain.chainIdentifierAbbr);
    this.taggingService.setTemplateTag(this.templateData.templateTag);
    this.setPageTitleAndMetaDescription(data);
    this.loadTemplate();
  }

  public setPageTitleAndMetaDescription(data) {
    this.title.setTitle(data?.homrData?.pageTitle || this.chain.metaTitle);
    const metaDescription = data?.homrData?.metaDescription || this.chain.metaDescription;
    this.metaService.addTag({ name: 'description', content: metaDescription }, true);
    this.metaService.addTag({ property: 'og:url', content: this.chain.metaCanonical }, true);
    this.seoService.setSearchAction(this.chain.metaCanonical); // Sets the search action script tag
  }

  public async loadTemplate() {
    const keys = Object.keys(this.templateData.content);
    for (const k in keys) {
      if (this.templateData.content[keys[k]]) {
        const key = keys[k];
        const data = this.templateData.content[key];
        if (this.components[key]) {
          await this.components[key](this.dynamicTemplateLoader, data, this.templateOutlet, {
            tagSectionNum: +k + 1,
            widgetTypeTag: key,
          });
        }
      }
    }

    if (isPlatformBrowser(this.platformId) && !this.document.body.classList.contains('aos')) {
      // Trigger the Post-render Event
      this.analyticsService.triggerAnalyticsEvent('NewPageView');
      triggerTargetView(`homepage--post-content-load`);
    }
  }

  public getEvergreen(chainString: string) {
    return this.homeDefault.getDefault(chainString);
  }

  public getCertonaRecommendations(certonaResponse: any) {
    // Set the response on the component
    this.store.set('CERTONA_RESPONSE', certonaResponse);
  }
}
